import { FC, useEffect, useState } from 'react'
import { Box, CircularProgress, Collapse, IconButton, List, ListItem, ListItemText, ListSubheader } from '@material-ui/core'
import { Error, ExpandLess, ExpandMore, NavigateNext, Search } from '@material-ui/icons'
import { FormattedMessage, useIntl } from 'react-intl'
import { wikiBrowserStyle } from './WikiBrowser.style'
import { useDispatch, useSelector } from 'react-redux'
import { wikiActions } from 'src/actions/WikiActions'
import { appStyle } from 'src/App.style'
import { CallResult } from 'src/CustomTypes'
import { ContextTag, WikiDocumentMeta } from 'src/dataSources/wiki/Wiki'
import WikiSearchComponent from '../wikisearch/WikiSearch'
import { RootState } from 'src/Store'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder } from '@fortawesome/free-regular-svg-icons'

/**
 * WikiBrowserComponentProps contains properties for the component.
 */
export interface WikiBrowserComponentProps {
  enableSearch: boolean
  enableBackNavigation: boolean
  showContent: boolean
  onBackPress: () => void
}

/**
 * WikiBrowserComponent displays available wiki document
 * @param props Properties for component.
 * @returns JSX component.
 */
export const WikiBrowserComponent: FC<WikiBrowserComponentProps> = (props: WikiBrowserComponentProps) => {
  const classes = wikiBrowserStyle()
  const appClasses = appStyle()
  const dispatch = useDispatch()
  const intl = useIntl()

  const documentsMeta = useSelector((state: RootState): WikiDocumentMeta[] => state.wikiState.documentsMeta)
  const categories = useSelector((state: RootState): string[] => state.wikiState.categories)
  const fetchStatus = useSelector((state: RootState): CallResult => state.wikiState.fetchDocumentsMetaStatus)
  const contextTags = useSelector((state: RootState): ContextTag[] => state.wikiState.contextTags)

  const [relevantMeta, setRelevantMeta] = useState<WikiDocumentMeta[]>([])
  const [otherMeta, setOtherMeta] = useState<WikiDocumentMeta[]>([])
  const hasRelevantMeta = relevantMeta.length > 0

  const [contextSectionOpen, setContextSectionOpen] = useState<boolean>(true)
  const [allSectionOpen, setAllSectionOpen] = useState<boolean>(true)

  const [searchSelected, setSearchSelected] = useState<boolean>(false)
  const [searchText, setSearchText] = useState<string>('')
  const showSearchResult = searchText.length > 2

  const fetched = fetchStatus === CallResult.Succeeded
  const error = fetchStatus === CallResult.Failed
  const running = fetchStatus === CallResult.Running

  useEffect(() => {
    let filteredMeta = documentsMeta
    if (searchText.length > 2) {
      filteredMeta = documentsMeta.filter(meta => meta.title.toLowerCase().includes(searchText.toLowerCase()))
    }

    const relevant = filteredMeta
      .sort((a, b) => a.title.localeCompare(b.title))
      .filter(meta => meta.tags.some(tag => contextTags.includes(tag)))
    const other = filteredMeta
      .sort((a, b) => a.title.localeCompare(b.title))
      .filter(meta => !meta.tags.some(tag => contextTags.includes(tag)))

    setRelevantMeta(relevant)
    setOtherMeta(other)
  }, [documentsMeta, contextTags, searchText])

  useEffect(() => {
    const handleKeyDown = (e: Event): void => {
      const event = ((e as unknown) as KeyboardEvent)
      if (event.metaKey && event.key === 'f') {
        event.preventDefault()
        setSearchSelected(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown, false)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false)
    }
  }, [setSearchSelected])

  const handleSearchClose = (): void => {
    setSearchSelected(false)
    setSearchText('')
  }

  return (
    <>
      <List
        component='nav'
        className={classes.list}
        aria-labelledby='nested-list-subheader'
        subheader={
          <ListSubheader component='div' className={`${classes.nestedListSubheader} ${props.enableBackNavigation ? classes.backButtonPadding : ''}`} id='nested-list-subheader'>
            {props.enableBackNavigation && (
              <div className={classes.backButton}>
                <IconButton size='medium' className={classes.backButtonIcon} onClick={props.onBackPress}>
                  <ArrowBackIosIcon />
                </IconButton>
              </div>
            )}
            <FormattedMessage id='wiki.browser.header' />
          </ListSubheader>
        }
      />
      {running && (
        <div className={classes.progress}>
          <CircularProgress size={30} className={appClasses.progressIcon} />
          <FormattedMessage id='common.loading' />
        </div>
      )}
      {error && (
        <div className={appClasses.errorMessage}>
          <Error fontSize='large' className={appClasses.errorIcon} />
          <FormattedMessage id='common.loading.error' />
        </div>
      )}
      {fetched && (
        <>
          {props.enableSearch && (
            <div className={classes.searchButton}>
              <IconButton
                size='medium'
                className={classes.searchButtonIcon}
                onClick={() => {
                  setSearchSelected(!searchSelected)
                  setSearchText('')
                }}
              >
                <Search />
              </IconButton>
            </div>
          )}
          <Collapse in={searchSelected} timeout='auto' unmountOnExit>
            <List component='div' disablePadding dense>
              <WikiSearchComponent onInputChange={input => setSearchText(input)} onClose={handleSearchClose} />
            </List>
          </Collapse>
          {props.showContent && (
            <>
              {showSearchResult && (
                <div className={`${relevantMeta.length === 0 ? classes.topMargin : ''}`}>
                  <ListItem
                    button
                    onClick={() => { setAllSectionOpen(!allSectionOpen) }}
                    className={classes.sectionListItem}
                  >
                    <ListItemText primary={intl.formatMessage({ id: 'wiki.browser.searchresult' })} className={classes.title} />
                  </ListItem>
                  <Collapse in={allSectionOpen} timeout='auto'>
                    {[...relevantMeta, ...otherMeta].map((document, index) => {
                      return (
                        <div key={index}>
                          {index > 0 && (
                            <div className={classes.divider} />
                          )}
                          <ListItem
                            button
                            className={classes.documentItem}
                            onClick={() => dispatch(wikiActions.fetchDocument(document.key))}
                          >
                            <ListItemText primary={document.title} className={classes.documentItem} />
                            <NavigateNext />
                          </ListItem>
                        </div>
                      )
                    })}
                  </Collapse>
                </div>
              )}
              {!showSearchResult && (
                <>
                  {hasRelevantMeta && (
                    <ListItem
                      button
                      onClick={() => { setContextSectionOpen(!contextSectionOpen) }}
                      className={classes.sectionListItem}
                    >
                      <ListItemText primary={intl.formatMessage({ id: 'wiki.browser.relevant' })} className={classes.title} />
                      {contextSectionOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                  )}
                  <Collapse in={contextSectionOpen} timeout='auto'>
                    {relevantMeta.map((document, index) => {
                      return (
                        <div key={index}>
                          {index > 0 && (
                            <div className={classes.divider} />
                          )}
                          <ListItem
                            button
                            className={classes.documentItem}
                            onClick={() => dispatch(wikiActions.fetchDocument(document.key))}
                          >
                            <ListItemText primary={document.title} />
                            <NavigateNext />
                          </ListItem>
                        </div>
                      )
                    })}
                  </Collapse>
                  <div className={`${relevantMeta.length === 0 ? classes.topMargin : ''}`}>
                    {categories.length > 0 && (
                      <ListItem
                        button
                        onClick={() => {
                          if (hasRelevantMeta) {
                            setAllSectionOpen(!allSectionOpen)
                          }
                        }}
                        className={`${classes.sectionListItem} ${hasRelevantMeta ? '' : classes.disabledListItem}`}
                      >
                        <ListItemText primary={intl.formatMessage({ id: 'wiki.browser.categories' })} className={classes.title} />
                        {hasRelevantMeta && (
                          <>
                            {allSectionOpen ? <ExpandLess /> : <ExpandMore />}
                          </>
                        )}
                      </ListItem>
                    )}
                    <Collapse in={allSectionOpen} timeout='auto'>
                      {categories.map((category, index) => {
                        return (
                          <div key={index}>
                            {index > 0 && (
                              <div className={classes.divider} />
                            )}
                            <ListItem
                              button
                              className={classes.documentItem}
                              onClick={() => dispatch(wikiActions.openCategory(category))}
                            >
                              <Box mr={2}>
                                <FontAwesomeIcon icon={faFolder} />
                              </Box>
                              <ListItemText primary={category} className={classes.documentItem} />
                              <NavigateNext />
                            </ListItem>
                          </div>
                        )
                      })}
                    </Collapse>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}

export default WikiBrowserComponent
